import type { FanChartPoint } from '@/entities/mid-term-prediction'

import { useState } from 'react'
import { format, parseISO } from 'date-fns'
import { useTranslation } from 'react-i18next'
import {
	Area,
	CartesianGrid,
	ComposedChart,
	Line,
	ReferenceLine,
	XAxis,
	YAxis,
} from 'recharts'

import { ChartContainer, ChartTooltip } from '@/shared/components/ui/chart'
import { cn } from '@/shared/lib/utils'

import { LEGEND_LABELS } from '../lib/labels'
import { ForecastFanTooltip } from './ForecastFanTooltip'

type LayerKey = 'actual' | 'median' | 'band50' | 'band90'

const LEGEND: { key: LayerKey; swatch: string }[] = [
	{ key: 'actual', swatch: 'h-0.5 w-3.5 rounded bg-muted-foreground' },
	{
		key: 'median',
		swatch: 'w-3.5 border-t-2 border-dashed border-primary',
	},
	{ key: 'band50', swatch: 'h-2.5 w-3 rounded-sm bg-primary/30' },
	{ key: 'band90', swatch: 'h-2.5 w-3 rounded-sm bg-primary/15' },
]

const formatTick = (date: string) => format(parseISO(date), 'd MMM')

export const ForecastFanChart = ({
	points,
	todayDate,
}: {
	points: FanChartPoint[]
	todayDate: string | null
}) => {
	const { t } = useTranslation()
	const [layers, setLayers] = useState<Record<LayerKey, boolean>>({
		actual: true,
		median: true,
		band50: true,
		band90: true,
	})

	const chartConfig = {
		actual: {
			label: t(LEGEND_LABELS.actual.key, {
				defaultValue: LEGEND_LABELS.actual.defaultValue,
			}),
			color: 'var(--muted-foreground)',
		},
		median: {
			label: t(LEGEND_LABELS.median.key, {
				defaultValue: LEGEND_LABELS.median.defaultValue,
			}),
			color: 'var(--primary)',
		},
	}

	const toggleLayer = (key: LayerKey) =>
		setLayers((prev) => ({ ...prev, [key]: !prev[key] }))

	return (
		<div data-testid='mtf-fan-chart'>
			<ChartContainer config={chartConfig} className='h-56 w-full'>
				<ComposedChart
					data={points}
					margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
				>
					<CartesianGrid vertical={false} strokeDasharray='3 3' />
					<ChartTooltip
						cursor={{ stroke: 'var(--border)' }}
						content={<ForecastFanTooltip />}
					/>
					<XAxis
						dataKey='date'
						tickFormatter={formatTick}
						minTickGap={40}
						tickLine={false}
						axisLine={false}
						fontSize={10}
					/>
					<YAxis
						width={36}
						tickLine={false}
						axisLine={false}
						allowDecimals={false}
						fontSize={10}
					/>
					{layers.band90 && (
						<Area
							dataKey='band90'
							fill='var(--color-median)'
							fillOpacity={0.12}
							stroke='none'
							isAnimationActive={false}
						/>
					)}
					{layers.band50 && (
						<Area
							dataKey='band50'
							fill='var(--color-median)'
							fillOpacity={0.24}
							stroke='none'
							isAnimationActive={false}
						/>
					)}
					{todayDate && (
						<ReferenceLine
							x={todayDate}
							stroke='var(--border)'
							strokeDasharray='3 3'
						/>
					)}
					{layers.actual && (
						<Line
							dataKey='actual'
							stroke='var(--color-actual)'
							strokeWidth={2}
							dot={false}
							isAnimationActive={false}
						/>
					)}
					{layers.median && (
						<Line
							dataKey='median'
							stroke='var(--color-median)'
							strokeWidth={2}
							strokeDasharray='5 3'
							dot={false}
							isAnimationActive={false}
						/>
					)}
				</ComposedChart>
			</ChartContainer>
			<div className='mt-2 flex flex-wrap gap-x-4 gap-y-1'>
				{LEGEND.map(({ key, swatch }) => (
					<button
						key={key}
						type='button'
						aria-pressed={layers[key]}
						data-testid={`mtf-legend-${key}`}
						onClick={() => toggleLayer(key)}
						className={cn(
							'inline-flex cursor-pointer items-center gap-1.5 text-xs text-foreground transition-opacity select-none',
							!layers[key] && 'opacity-40'
						)}
					>
						<span className={swatch} />
						{t(LEGEND_LABELS[key].key, {
							defaultValue: LEGEND_LABELS[key].defaultValue,
						})}
					</button>
				))}
			</div>
		</div>
	)
}
